import { useCallback, useEffect, useState } from 'react';
import type { AxiosInstance } from 'axios';
import { useSearchParams } from 'react-router-dom';
import { CasosListSkeleton } from '@/components/skeletons';
import { FalhaComSaida, KpiCard } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { PendentesDoResponsavel } from '@/components/Certificacao/PendentesDoResponsavel';
import { PendentesTabela } from '@/components/Certificacao/PendentesTabela';
import { RevisaoPanel, type AssinaturaIcpDoPainel } from '@/components/Certificacao/RevisaoPanel';
import { mensagemDoEstado } from '@/components/Certificacao/assinaturaIcp';
import { mapCertificacaoError } from '@/components/Certificacao/messages';
import {
  assinarComReconciliacao,
  type DesfechoDaAssinatura,
} from '@/components/Certificacao/assinatura';
import {
  assumirRequisicao,
  CertificacaoError,
  type ChecklistRevisao,
  type CertificacoesPendentes,
  type CertificacaoPendente,
  getPendentes,
} from '@/lib/certificacao';
import type { EstadoDaAssinaturaIcp, PapelNaAssinatura } from '@/lib/assinatura_icp';
import { getPerfil, type PerfilData } from '@/lib/perfil';
import { formatarDataHora } from '@/lib/trilha_format';

const t = ptBR.certificacao;

function _mensagem(err: unknown): string {
  return mapCertificacaoError(err instanceof CertificacaoError ? err.reason : null);
}

/** E147.F3: a assinatura reconciliada mostra a que estava gravada. */
function _gravada(
  desfecho: Extract<DesfechoDaAssinatura, { desfecho: 'assinada' }>,
): string | null {
  const { assinatura } = desfecho;
  if (assinatura.reconciliada !== true) return null;
  return t.assinaturaGravada
    .replace('{quando}', formatarDataHora(assinatura.assinado_em))
    .replace('{sha}', assinatura.documento_sha256.slice(0, 12));
}

/** E164.F2 (ADR-0113): o papel na assinatura com certificado digital e o CPF, do perfil. */
function assinaturaComCertificado(
  perfil: PerfilData | null,
  client: AxiosInstance,
  onConcluida: (estado: EstadoDaAssinaturaIcp) => void,
): { icp?: AssinaturaIcpDoPainel } {
  if (perfil === null) return {};
  const papel: PapelNaAssinatura =
    perfil.responsavel_tecnico === true ? 'responsavel_tecnico' : 'assistente_tecnico';
  return { icp: { client, papel, cpfCadastrado: Boolean(perfil.cpf), onConcluida } };
}

function SecaoDoResponsavel({
  perfil,
  client,
  onAssinados,
}: {
  perfil: PerfilData | null;
  client: AxiosInstance;
  onAssinados: () => Promise<void>;
}) {
  if (perfil?.responsavel_tecnico !== true) return null;
  return <PendentesDoResponsavel client={client} onAssinados={() => void onAssinados()} />;
}

export function CertificacaoPage() {
  const apiClient = useApiClient();
  const [pendentes, setPendentes] = useState<CertificacoesPendentes | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erroLista, setErroLista] = useState<string | null>(null);
  const [itemAberto, setItemAberto] = useState<CertificacaoPendente | null>(null);
  const [abrindoId, setAbrindoId] = useState<string | null>(null);
  const [assinando, setAssinando] = useState(false);
  const [erroAcao, setErroAcao] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);
  const [coincidencia, setCoincidencia] = useState(false);
  const [gravada, setGravada] = useState<string | null>(null);
  // E164.F2 (ADR-0113): o papel na assinatura com certificado digital e o CPF vêm do perfil.
  const [perfil, setPerfil] = useState<PerfilData | null>(null);

  const recarregar = useCallback(async () => {
    setCarregando(true);
    setErroLista(null);
    try {
      setPendentes(await getPendentes(apiClient));
    } catch (err) {
      setPendentes(null);
      setErroLista(_mensagem(err));
    } finally {
      setCarregando(false);
    }
  }, [apiClient]);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  useEffect(() => {
    getPerfil(apiClient).then(setPerfil, () => setPerfil(null));
  }, [apiClient]);

  // E169.F1 (GUS-113): a conferência final do caso manda para cá com a requisição dele; o item
  // abre sozinho (abrir é assumir), uma vez, quando a lista o traz.
  const [params] = useSearchParams();
  const pedida = params.get('requisicao');
  const [abertaPelaConferencia, setAbertaPelaConferencia] = useState(false);
  useEffect(() => {
    if (pedida === null || abertaPelaConferencia || pendentes === null) return;
    const item = pendentes.itens.find((i) => i.requisicao_id === pedida);
    setAbertaPelaConferencia(true);
    if (item !== undefined) void handleAbrirRevisao(item);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- abre uma vez, com a lista lida
  }, [pedida, pendentes, abertaPelaConferencia]);

  function handleConcluidaComCertificado(estado: EstadoDaAssinaturaIcp) {
    setItemAberto(null);
    setErroAcao(null);
    setGravada(null);
    setCoincidencia(false);
    setSucesso(mensagemDoEstado(estado));
    void recarregar();
  }

  // Abrir a revisão É assumir o item: sem isso o certificador preencheria o
  // checklist para descobrir no clique final que o item é de outra pessoa. A
  // disputa (409) aparece aqui, antes do trabalho.
  async function handleAbrirRevisao(item: CertificacaoPendente) {
    setErroAcao(null);
    setSucesso(null);
    setAbrindoId(item.requisicao_id);
    try {
      await assumirRequisicao(apiClient, item.requisicao_id);
      setItemAberto(item);
    } catch (err) {
      setItemAberto(null);
      setErroAcao(_mensagem(err));
      // A lista mudou por baixo (outro assumiu, ou já assinaram):
      // recarregar é o que devolve a tela à verdade do servidor.
      void recarregar();
    } finally {
      setAbrindoId(null);
    }
  }

  async function handleAssinar(checklist: ChecklistRevisao) {
    if (itemAberto === null) return;
    setAssinando(true);
    setErroAcao(null);
    try {
      const desfecho = await assinarComReconciliacao(
        apiClient,
        itemAberto.requisicao_id,
        checklist,
      );
      if (desfecho.desfecho !== 'assinada') {
        setErroAcao(
          desfecho.desfecho === 'nao_assinada'
            ? t.assinaturaNaoRegistrada
            : t.assinaturaSemConfirmacao,
        );
        return;
      }
      setItemAberto(null);
      setSucesso(
        desfecho.assinatura.reconciliada === true ? t.assinaturaReconciliada : t.assinaturaOk,
      );
      setGravada(_gravada(desfecho));
      setCoincidencia(desfecho.assinatura.mesmo_autor_producao);
      await recarregar();
    } catch (err) {
      setErroAcao(_mensagem(err));
    } finally {
      setAssinando(false);
    }
  }

  return (
    <section
      data-testid="certificacao-page"
      className="mx-auto flex w-full max-w-5xl flex-col gap-sc-6"
    >
      <header>
        <p className="mb-1.5 font-titulo text-rotulo uppercase text-fg-dim">{t.eyebrow}</p>
        <h1 className="text-display-md text-fg">{t.pageTitle}</h1>
        <p className="mt-2 max-w-2xl text-body-sm text-fg-muted">{t.intro}</p>
      </header>

      {pendentes !== null ? <Indicadores indicadores={pendentes.indicadores} /> : null}

      {sucesso !== null ? (
        <div
          role="status"
          data-testid="certificacao-sucesso"
          className="border border-success bg-success-50 px-sc-3 py-2 text-body-sm text-success-700"
        >
          <p>{sucesso}</p>
          {gravada !== null ? (
            <p data-testid="certificacao-gravada" className="mt-1">
              {gravada}
            </p>
          ) : null}
          {coincidencia ? (
            <p data-testid="certificacao-coincidencia" className="mt-1">
              {t.assinaturaCoincidencia}
            </p>
          ) : null}
        </div>
      ) : null}

      {erroAcao !== null && itemAberto === null ? (
        <p
          role="alert"
          data-testid="certificacao-erro-acao"
          className="border border-danger bg-danger-50 px-sc-3 py-2 text-body-sm text-danger-700"
        >
          {erroAcao}
        </p>
      ) : null}

      {itemAberto !== null ? (
        <RevisaoPanel
          item={itemAberto}
          assinando={assinando}
          erro={erroAcao}
          onAssinar={(checklist) => void handleAssinar(checklist)}
          onFechar={() => {
            setItemAberto(null);
            setErroAcao(null);
          }}
          {...assinaturaComCertificado(perfil, apiClient, handleConcluidaComCertificado)}
        />
      ) : null}

      <SecaoDoResponsavel perfil={perfil} client={apiClient} onAssinados={recarregar} />

      <ListaSecao
        carregando={carregando}
        erro={erroLista}
        itens={pendentes?.itens ?? []}
        itemAberto={itemAberto?.requisicao_id ?? null}
        abrindoId={abrindoId}
        onAbrirRevisao={(item) => void handleAbrirRevisao(item)}
        onRetry={() => void recarregar()}
      />
    </section>
  );
}

interface ListaSecaoProps {
  carregando: boolean;
  erro: string | null;
  itens: CertificacaoPendente[];
  itemAberto: string | null;
  abrindoId: string | null;
  onAbrirRevisao: (item: CertificacaoPendente) => void;
  onRetry: () => void;
}

/** Carregando / erro com nova tentativa / tabela — os três estados da lista. */
function ListaSecao({
  carregando,
  erro,
  itens,
  itemAberto,
  abrindoId,
  onAbrirRevisao,
  onRetry,
}: ListaSecaoProps) {
  if (carregando) {
    // E200.F3 (GUS-220): esqueleto estrutural da tabela, anunciado para o leitor de tela.
    return (
      <div data-testid="pendentes-loading" role="status" aria-label={t.pendentesLoading}>
        <CasosListSkeleton rows={4} />
      </div>
    );
  }
  if (erro !== null) {
    // E200.F3 (ADR-0143 §4): a falha com saída — tentar de novo ou voltar aos casos.
    return (
      <FalhaComSaida
        mensagem={erro}
        onTentarDeNovo={onRetry}
        voltar={{ para: '/casos', rotulo: ptBR.falhaComSaida.voltarAosCasos }}
        data-testid="pendentes-error"
      />
    );
  }
  return (
    <PendentesTabela
      itens={itens}
      itemAberto={itemAberto}
      abrindoId={abrindoId}
      onAbrirRevisao={onAbrirRevisao}
    />
  );
}

function Indicadores({ indicadores }: { indicadores: CertificacoesPendentes['indicadores'] }) {
  return (
    <div
      className="grid grid-cols-1 gap-sc-4 sm:grid-cols-3"
      data-testid="certificacao-indicadores"
    >
      <KpiCard
        title={t.indicadorPendentes}
        value={indicadores.na_fila}
        data-testid="indicador-pendentes"
      />
      <KpiCard
        title={t.indicadorVencemHoje}
        value={indicadores.vencem_hoje}
        data-testid="indicador-vencem-hoje"
      />
      <KpiCard
        title={t.indicadorAssinadosHoje}
        value={indicadores.assinados_hoje}
        data-testid="indicador-assinados-hoje"
      />
    </div>
  );
}

export default CertificacaoPage;
